import { useState, useCallback, useEffect, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import CodeIcon from '@mui/icons-material/Code';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';

/**
 * Convert plain text (with markdown-style headings/lists) to simple HTML
 * so TipTap's visual mode can render it properly.
 */
function plainTextToHtml(text: string): string {
  if (!text) return '';
  // If it already looks like HTML, return as-is
  if (text.trimStart().startsWith('<')) return text;

  const lines = text.split('\n');
  const htmlParts: string[] = [];
  let inList = false;

  for (const line of lines) {
    const trimmed = line.trimEnd();

    // Headings
    if (trimmed.startsWith('### ')) {
      if (inList) { htmlParts.push('</ul>'); inList = false; }
      htmlParts.push(`<h3>${trimmed.slice(4)}</h3>`);
    } else if (trimmed.startsWith('## ')) {
      if (inList) { htmlParts.push('</ul>'); inList = false; }
      htmlParts.push(`<h2>${trimmed.slice(3)}</h2>`);
    } else if (trimmed.startsWith('# ')) {
      if (inList) { htmlParts.push('</ul>'); inList = false; }
      htmlParts.push(`<h1>${trimmed.slice(2)}</h1>`);
    }
    // List items
    else if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
      if (!inList) { htmlParts.push('<ul>'); inList = true; }
      // Handle **bold** within list items
      const itemText = trimmed.slice(2).replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
      htmlParts.push(`<li>${itemText}</li>`);
    }
    // Empty line = paragraph break
    else if (trimmed === '') {
      if (inList) { htmlParts.push('</ul>'); inList = false; }
    }
    // Regular text
    else {
      if (inList) { htmlParts.push('</ul>'); inList = false; }
      const formatted = trimmed.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
      htmlParts.push(`<p>${formatted}</p>`);
    }
  }
  if (inList) htmlParts.push('</ul>');

  return htmlParts.join('');
}

/**
 * Convert TipTap HTML back to plain text for storage.
 * Uses a temporary DOM element to extract text with structure preserved.
 */
function htmlToPlainText(html: string): string {
  if (!html) return '';
  // If it doesn't look like HTML, return as-is
  if (!html.trimStart().startsWith('<')) return html;

  const div = document.createElement('div');
  div.innerHTML = html;
  const parts: string[] = [];

  for (const node of div.childNodes) {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent?.trim();
      if (text) parts.push(text);
      continue;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) continue;

    const el = node as HTMLElement;
    const tag = el.tagName.toLowerCase();

    if (tag === 'h1') parts.push(`# ${el.textContent}`);
    else if (tag === 'h2') parts.push(`## ${el.textContent}`);
    else if (tag === 'h3') parts.push(`### ${el.textContent}`);
    else if (tag === 'ul' || tag === 'ol') {
      const items = el.querySelectorAll('li');
      items.forEach((li) => {
        // Convert <strong> back to **bold**
        let text = li.innerHTML.replace(/<strong>(.*?)<\/strong>/g, '**$1**');
        // Strip any remaining HTML tags
        const tmp = document.createElement('span');
        tmp.innerHTML = text;
        text = tmp.textContent ?? text;
        parts.push(`- ${text}`);
      });
    }
    else if (tag === 'blockquote') parts.push(`> ${el.textContent}`);
    else if (tag === 'pre') parts.push(`\`\`\`\n${el.textContent}\n\`\`\``);
    else {
      // Convert <strong> back to **bold** in paragraphs
      let text = el.innerHTML.replace(/<strong>(.*?)<\/strong>/g, '**$1**');
      const tmp = document.createElement('span');
      tmp.innerHTML = text;
      text = tmp.textContent ?? text;
      parts.push(text);
    }
  }

  return parts.join('\n\n');
}

interface TipTapEditorProps {
  content: string;
  onChange: (content: string) => void;
  placeholder?: string;
  maxLength?: number;
  readOnly?: boolean;
}

export function TipTapEditor({
  content,
  onChange,
  placeholder = 'Enter prompt content...',
  maxLength = 50000,
  readOnly = false,
}: TipTapEditorProps) {
  const [mode, setMode] = useState<'visual' | 'source'>('source');
  // Track whether the latest change came from the editor itself
  const isInternalUpdate = useRef(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Placeholder.configure({ placeholder }),
    ],
    content: plainTextToHtml(content),
    editable: !readOnly && mode === 'visual',
    onUpdate: ({ editor: ed }) => {
      isInternalUpdate.current = true;
      onChange(htmlToPlainText(ed.getHTML()));
    },
  });

  // Sync external content changes into the editor (e.g. async load, tab switch)
  useEffect(() => {
    if (!editor) return;
    if (isInternalUpdate.current) {
      isInternalUpdate.current = false;
      return;
    }
    // Only update if the editor's current text differs from the incoming content
    const editorText = htmlToPlainText(editor.getHTML());
    if (editorText !== content) {
      editor.commands.setContent(plainTextToHtml(content), { emitUpdate: false });
    }
  }, [content, editor]);

  const handleSourceChange = useCallback(
    (value: string) => {
      isInternalUpdate.current = true;
      onChange(value);
      if (editor) {
        editor.commands.setContent(plainTextToHtml(value), { emitUpdate: false });
      }
    },
    [editor, onChange]
  );

  const handleModeChange = useCallback(
    (_: React.MouseEvent<HTMLElement>, newMode: 'visual' | 'source' | null) => {
      if (!newMode || !editor) return;
      if (newMode === 'visual') {
        // Reload plain text content into editor as HTML
        editor.commands.setContent(plainTextToHtml(content), { emitUpdate: false });
        editor.setEditable(!readOnly);
      }
      if (newMode === 'source') {
        // Sync editor HTML back to plain text
        const text = htmlToPlainText(editor.getHTML());
        isInternalUpdate.current = true;
        onChange(text);
      }
      setMode(newMode);
    },
    [editor, onChange, content, readOnly]
  );

  const charCount = content.length;

  if (!editor) return null;

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        overflow: 'hidden',
      }}
    >
      {/* Toolbar */}
      {!readOnly && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            px: 1,
            py: 0.5,
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'action.hover',
            flexWrap: 'wrap',
          }}
        >
          {mode === 'visual' && (
            <>
              <Tooltip title="Heading 1">
                <IconButton
                  size="small"
                  onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                  color={editor.isActive('heading', { level: 1 }) ? 'primary' : 'default'}
                >
                  <Typography variant="caption" sx={{
                    fontWeight: 700
                  }}>
                    H1
                  </Typography>
                </IconButton>
              </Tooltip>
              <Tooltip title="Heading 2">
                <IconButton
                  size="small"
                  onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                  color={editor.isActive('heading', { level: 2 }) ? 'primary' : 'default'}
                >
                  <Typography variant="caption" sx={{
                    fontWeight: 700
                  }}>
                    H2
                  </Typography>
                </IconButton>
              </Tooltip>
              <Tooltip title="Heading 3">
                <IconButton
                  size="small"
                  onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                  color={editor.isActive('heading', { level: 3 }) ? 'primary' : 'default'}
                >
                  <Typography variant="caption" sx={{
                    fontWeight: 700
                  }}>
                    H3
                  </Typography>
                </IconButton>
              </Tooltip>
              <Divider orientation="vertical" flexItem sx={{ height: 24, my: 'auto', mx: 0.5 }} />
              <Tooltip title="Bold">
                <IconButton
                  size="small"
                  onClick={() => editor.chain().focus().toggleBold().run()}
                  color={editor.isActive('bold') ? 'primary' : 'default'}
                >
                  <FormatBoldIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Italic">
                <IconButton
                  size="small"
                  onClick={() => editor.chain().focus().toggleItalic().run()}
                  color={editor.isActive('italic') ? 'primary' : 'default'}
                >
                  <FormatItalicIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Divider orientation="vertical" flexItem sx={{ height: 24, my: 'auto', mx: 0.5 }} />
              <Tooltip title="Bullet list">
                <IconButton
                  size="small"
                  onClick={() => editor.chain().focus().toggleBulletList().run()}
                  color={editor.isActive('bulletList') ? 'primary' : 'default'}
                >
                  <FormatListBulletedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Numbered list">
                <IconButton
                  size="small"
                  onClick={() => editor.chain().focus().toggleOrderedList().run()}
                  color={editor.isActive('orderedList') ? 'primary' : 'default'}
                >
                  <FormatListNumberedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Blockquote">
                <IconButton
                  size="small"
                  onClick={() => editor.chain().focus().toggleBlockquote().run()}
                  color={editor.isActive('blockquote') ? 'primary' : 'default'}
                >
                  <FormatQuoteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Code block">
                <IconButton
                  size="small"
                  onClick={() => editor.chain().focus().toggleCodeBlock().run()}
                  color={editor.isActive('codeBlock') ? 'primary' : 'default'}
                >
                  <CodeIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <ToggleButtonGroup
            size="small"
            value={mode}
            exclusive
            onChange={handleModeChange}
          >
            <ToggleButton value="visual">
              <Tooltip title="Visual editor">
                <VisibilityIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="source">
              <Tooltip title="Source (HTML)">
                <EditIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}
      {/* Editor content */}
      {mode === 'visual' ? (
        <Box
          sx={{
            minHeight: 300,
            maxHeight: 600,
            overflow: 'auto',
            p: 2,
            '& .tiptap': {
              outline: 'none',
              minHeight: 280,
              '& h1': { fontSize: '1.5rem', fontWeight: 700, mt: 2, mb: 1 },
              '& h2': { fontSize: '1.25rem', fontWeight: 600, mt: 2, mb: 1 },
              '& h3': { fontSize: '1.1rem', fontWeight: 600, mt: 1.5, mb: 0.5 },
              '& p': { mb: 1 },
              '& ul, & ol': { pl: 3, mb: 1 },
              '& blockquote': {
                borderLeft: 3,
                borderColor: 'divider',
                pl: 2,
                ml: 0,
                color: 'text.secondary',
              },
              '& pre': {
                bgcolor: 'action.hover',
                p: 1.5,
                borderRadius: 1,
                fontFamily: 'monospace',
                fontSize: '0.875rem',
                overflow: 'auto',
              },
              '& .is-editor-empty:first-child::before': {
                content: 'attr(data-placeholder)',
                float: 'left',
                color: 'text.disabled',
                pointerEvents: 'none',
                height: 0,
              },
            },
          }}
        >
          <EditorContent editor={editor} />
        </Box>
      ) : (
        <TextField
          fullWidth
          multiline
          minRows={12}
          maxRows={24}
          value={content}
          onChange={(e) => handleSourceChange(e.target.value)}
          disabled={readOnly}
          sx={{ '& .MuiOutlinedInput-notchedOutline': { border: 'none' } }}
          slotProps={{
            htmlInput: { maxLength, style: { fontFamily: 'monospace', fontSize: '0.875rem' } }
          }}
        />
      )}
      {/* Status bar */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          px: 1.5,
          py: 0.5,
          borderTop: 1,
          borderColor: 'divider',
          bgcolor: 'action.hover',
        }}
      >
        <Typography variant="caption" sx={{
          color: "text.secondary"
        }}>
          {charCount.toLocaleString()} {maxLength ? `/ ${maxLength.toLocaleString()}` : ''} chars
        </Typography>
      </Box>
    </Box>
  );
}
